import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Form,
  Loader,
  Row,
  Col,
  Button,
  Iconography,
  useSubscribe,
  useValues,
  FileUpload,
} from '@lucid/lego';
import type { FormRef } from '@lucid/lego';
import { useFileUpload } from '../../hooks/useFileUpload';
import { useLabels } from '../../hooks/useLabels';
import { useStore } from '../../store/store';
import {
  formatToUTCDate,
  getCurrencySymbol,
  getCurrencyCodeForLocale,
  formatDate,
  convertToContentfulLocale,
  getContentfulLocaleFromLanguage,
  formatToOffsetDateTime,
  parseLocalePrice,
  getCountryCodeFromLocale,
} from '../../utils/tools';
import { createQuoteFormFields } from './formFields';
import { useGetLenders } from '../../api/services/lupp/quotes/quotes';
import {
  fetchDiscountGuidePdf,
  useGetIncentiveLimits,
} from '../../api/services/lupp/incentives/incentives';
import type { IncentiveLimits } from '../../api/services/lupp/incentives/dto.incentives';
import type { TransformedCheckoutDetail } from '../../api/services/lupp/inventory/transformers.checkout';
import type { BusinessSearchResult } from '../../api/services/lupp/order/dto.order';
import {
  applyBusinessSelection,
  buildBusinessInfo,
  buildFinancingDetails,
  buildGrandTotalCalculationParams,
  calculateDiscountTotals,
  calculatePricing,
  hasChargingCreditOfferSelected,
  hasTradeInSupportSelected,
  mapRequestedIncentivesToArray,
  resolveLenderFields,
  roundTo2,
  syncDiscountFields,
} from './utils';
import type { InventoryTableRow } from '../Configurator/utils';
import { BusinessNameSearch } from '../BusinessNameSearch/BusinessNameSearch';
import { FileHubApi } from '../../api/services/lupp/lupp.service';
import { DocumentsList } from './DocumentsList/DocumentsList';
import styles from './Checkout.module.scss';
import VehicleCard from './VehicleCard/VehicleCard';
import Docusign from './Docusign/Docusign';
import {
  useUpdateQuote,
  useCreateQuote,
  useDownloadQuote,
  useSendDocusign,
} from '../../api/services/lupp/quotes/quotes';
import {
  useGetCheckoutByShortcode,
  CHECKOUT_ERROR_CODES,
} from '../../api/services/lupp/inventory/inventory';
import type { SendDocusignFormData } from './Docusign/Docusign';
import type { UploadQuoteDocumentRequest } from '../../api/services/lupp/quotes/dto.quotes';
import { DOCUMENT_CATEGORY, FILEHUB_MODULE_NAME } from '../../constants/constants';
import CheckoutModal from './Modal/Modal';
import {
  SUCCESS_MESSAGE,
  SUCCESS_UPDATE_MESSAGE,
  VALIDATION_ERROR,
  DISCOUNT_ENABLED_PAYMENT_METHODS,
  PAYMENT_METHOD_VALUES,
  LOAN_PAYMENT_METHODS,
  QUOTE_FORM_FIELDS,
  DOCUSIGN_SENT_STATUSES,
  VEHICLE_UNAVAILABLE_TITLE,
  VEHICLE_UNAVAILABLE_MESSAGE,
} from './constants';

/**
 * Uploaded file structure from FileUpload component
 */
type UploadedFile = {
  url: string;
  name: string;
};

/**
 * Quote form data type representing all form fields
 */
type QuoteFormData = {
  prospectType: string;
  preferredPaymentMethod: string;
  deliveryTimeline: Date | string;
  companyName?: string;
  companyDuns?: string; // D&B DUNS number for company
  companyTaxId?: string;
  invoiceAddress?: string;
  businessCity?: string;
  invoiceCountry?: string;
  businessPostalCode?: string;
  financeCompanyName?: string;
  financeDuns?: string; // D&B DUNS number for finance company
  financeCompanyAddress?: string;
  financeCity?: string;
  financePostalCode?: string;
  financeCountry?: string;
  financeVat?: string;
  uploadBankQuotation?: UploadedFile[];
  quoteId?: string;
  financeProvider?: string;
  agentSubventionAmount?: string;
  monthlyPaymentInclVat?: string;
  downpayment?: string;
  requestedIncentives?: string[];
  cashDiscount?: string;
  percentageDiscount?: string;
  agentDiscountAmount?: string;
  agentDiscountPercentage?: string;
  lucidDiscountAmount?: string;
  lucidDiscountPercentage?: string;
};

type DiscountBreakdown = Pick<
  QuoteFormData,
  | 'agentDiscountAmount'
  | 'agentDiscountPercentage'
  | 'lucidDiscountAmount'
  | 'lucidDiscountPercentage'
>;

// Agent and Lucid portions combine into the total; quotes saved before the split keep their totals.
const resolveDiscountTotals = (formData: QuoteFormData) => {
  const hasBreakdown = [
    formData.agentDiscountAmount,
    formData.agentDiscountPercentage,
    formData.lucidDiscountAmount,
    formData.lucidDiscountPercentage,
  ].some(Boolean);
  if (!hasBreakdown) {
    return {
      cashDiscount: formData.cashDiscount,
      percentageDiscount: formData.percentageDiscount,
    };
  }
  return calculateDiscountTotals({
    agentDiscountAmount: formData.agentDiscountAmount,
    agentDiscountPercentage: formData.agentDiscountPercentage,
    lucidDiscountAmount: formData.lucidDiscountAmount,
    lucidDiscountPercentage: formData.lucidDiscountPercentage,
  });
};

const exceedsDiscountBreakdownLimit = (
  subtotal: number,
  breakdown: DiscountBreakdown,
  limits: IncentiveLimits
): boolean => {
  const agentAmount = parseLocalePrice(breakdown.agentDiscountAmount);
  const lucidAmount = parseLocalePrice(breakdown.lucidDiscountAmount);
  if (
    parseLocalePrice(breakdown.agentDiscountPercentage) > limits.maximumAgentDiscountPercentage ||
    parseLocalePrice(breakdown.lucidDiscountPercentage) > limits.maximumLucidDiscountPercentage
  ) {
    return true;
  }
  if (subtotal <= 0) return false;
  return (
    agentAmount > roundTo2((limits.maximumAgentDiscountPercentage / 100) * subtotal) ||
    lucidAmount > roundTo2((limits.maximumLucidDiscountPercentage / 100) * subtotal)
  );
};

const exceedsPartnerCompensationLimit = (
  configurationSubtotal: number,
  cashDiscountValue: number,
  percentageDiscountValue: number,
  agentSubventionValue: number,
  maximumPercentage: number
): boolean => {
  if (percentageDiscountValue > maximumPercentage) return true;
  if (configurationSubtotal <= 0) return false;

  const maxAllowedDiscount =
    Math.round((maximumPercentage / 100) * configurationSubtotal * 100) / 100;
  return cashDiscountValue > maxAllowedDiscount || agentSubventionValue > maxAllowedDiscount;
};

const resolvePriceAdjustmentValue = (
  preferredPaymentMethod: string,
  configurationSubtotal: number,
  cashDiscountValue: number,
  percentageDiscountValue: number,
  agentSubventionValue: number
): number => {
  if (preferredPaymentMethod.toLowerCase() === 'lfs lease') return 0;
  if (cashDiscountValue > 0) return cashDiscountValue;
  if (percentageDiscountValue > 0) {
    return (Math.min(percentageDiscountValue, 100) / 100) * configurationSubtotal;
  }
  return agentSubventionValue;
};

/**
 * Prospect details for quote creation
 */
export type ProspectDetails = {
  prospectId: string;
  prospectName?: string;
  quoteId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  stateProvince?: string;
};

/**
 * Props for the Checkout component
 */
export type CheckoutProps = {
  /**
   * The selected vehicle data
   */
  vehicle: InventoryTableRow | null;
  /**
   * Checkout detail data from the API (transformed)
   */
  checkoutData: TransformedCheckoutDetail | null;
  /**
   * Whether the checkout data is loading
   */
  isLoading: boolean;
  /**
   * Callback to go back to the inventory list
   */
  onBack: () => void;
  /**
   * Custom data-testid for the component
   * @default 'checkout'
   */
  dataTestId?: string;
  /**
   * Prospect details including ID, name and quote ID
   */
  prospectDetails?: ProspectDetails;
  /**
   * Current docusign status from quote API
   */
  docusignStatus?: string;
  /**
   * Current quote status from quote API
   */
  currentStatus?: string;
  /**
   * Error message when checkout data fails to load
   */
  error?: string | null;
  /**
   * Callback when a quote is successfully created (for URL update)
   */
  onQuoteCreated?: (quoteId: string) => void;
};

/**
 * Renders a checkout section title and optional guide button through the Form custom-field registry.
 * @component
 * @param props - Localized labels, PDF action and form theme.
 * @returns The checkout incentives header.
 * @example
 * <IncentivesHeader title="Incentives" discountGuideLabel="Discount Guide" />
 */
const IncentivesHeader = ({
  title,
  discountGuideLabel,
  onDiscountGuideClick,
  isDiscountGuideLoading,
  theme = 'light',
}: {
  title?: string;
  discountGuideLabel?: string;
  onDiscountGuideClick?: () => void;
  isDiscountGuideLoading?: boolean;
  theme?: 'light' | 'dark';
}) => (
  <Box className={styles.incentivesHeader}>
    <Typography
      variant="subtitle"
      size="small"
      color={theme === 'dark' ? 'black' : 'white'}
      theme={theme}
    >
      {title}
    </Typography>
    {onDiscountGuideClick && (
      <Button
        type="secondary"
        color="neutral"
        size="small"
        theme="light"
        label={discountGuideLabel}
        dataTestId="checkout-discount-guide"
        gaComponent="checkout"
        disabled={isDiscountGuideLoading}
        handleClick={(event) => {
          event.preventDefault();
          onDiscountGuideClick?.();
        }}
      />
    )}
  </Box>
);

/**
 * Checkout component displays vehicle details and quote creation form
 * @component
 * @param props - Component props
 * @param props.vehicle - The selected vehicle data
 * @param props.checkoutData - Checkout detail data from the API
 * @param props.isLoading - Whether the checkout data is loading
 * @param props.onBack - Callback to go back to inventory
 * @param props.dataTestId - Test ID for the component
 * @param props.prospectDetails - Prospect details including ID, name and quote ID
 * @param props.docusignStatus - Current docusign status from API
 * @param props.currentStatus - Current quote status from API
 * @param props.onQuoteCreated - Callback when a quote is successfully created
 * @returns JSX.Element
 *
 * @example
 * <Checkout
 *   vehicle={selectedVehicle}
 *   checkoutData={checkoutDetails}
 *   isLoading={false}
 *   onBack={() => setSelectedVehicle(null)}
 *   prospectDetails={{ prospectId: '123', prospectName: 'John Doe' }}
 * />
 */
const Checkout: React.FC<CheckoutProps> = ({
  vehicle,
  checkoutData,
  isLoading,
  onBack,
  dataTestId = 'checkout',
  prospectDetails,
  docusignStatus: initialDocusignStatus,
  currentStatus,
  error,
  onQuoteCreated,
}) => {
  const theme = useStore((state) => state.theme);
  const locale = useStore((state) => state.locale);
  const language = useStore((state) => state.language);

  const { data: labels } = useLabels({
    id: '6dhtpdHCYn1jTZ1cRAXtJR',
    locale: convertToContentfulLocale(locale),
  });

  // State declarations
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDiscountGuideLoading, setIsDiscountGuideLoading] = useState(false);
  const discountGuideUrls = useRef<string[]>([]);

  useEffect(() => {
    const urls = discountGuideUrls.current;
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  const handleDiscountGuideClick = useCallback(async () => {
    if (isDiscountGuideLoading) return;

    // Reserve the tab during the click so the asynchronous request is not popup-blocked.
    const pdfTab = window.open('about:blank', '_blank');
    if (!pdfTab) {
      setErrorMessage(
        labels?.['checkout.discountGuide.popupBlocked'] ??
          'Please allow pop-ups to open the Discount Guide.'
      );
      return;
    }

    pdfTab.opener = null;
    setErrorMessage(null);
    setIsDiscountGuideLoading(true);
    try {
      const pdf = await fetchDiscountGuidePdf();
      const url = URL.createObjectURL(pdf);
      discountGuideUrls.current.push(url);
      pdfTab.location.replace(url);
    } catch (error) {
      pdfTab.close();
      console.error('Failed to open discount guide:', error);
      setErrorMessage(
        labels?.['checkout.discountGuide.error'] ??
          'Unable to open the Discount Guide. Please try again.'
      );
    } finally {
      setIsDiscountGuideLoading(false);
    }
  }, [isDiscountGuideLoading, labels]);
  const [isVehicleUnavailable, setIsVehicleUnavailable] = useState(false);
  const [isQuoteSaved, setIsQuoteSaved] = useState(false);
  const [savedQuoteId, setSavedQuoteId] = useState<string | undefined>(prospectDetails?.quoteId);
  const [documentMetadata, setDocumentMetadata] = useState<UploadQuoteDocumentRequest | undefined>(
    undefined
  );
  const [discountValues, setDiscountValues] = useState<
    {
      cashDiscount?: string;
      percentageDiscount?: string;
      agentSubventionAmount?: string;
    } & DiscountBreakdown
  >({});
  const [requestedIncentives, setRequestedIncentives] = useState<string[]>([]);
  // Store original API discount values to restore when switching payment methods
  const apiDiscountValuesRef = useRef<
    {
      cashDiscount?: string;
      percentageDiscount?: string;
      agentSubventionAmount?: string;
    } & DiscountBreakdown
  >({});

  const formRef = useRef<FormRef>(null);
  const getFormValues = useValues<string>(formRef);
  // Date format follows language preference; falls back to store locale if no language override
  const languageBasedLocale = useMemo(
    () =>
      language ? getContentfulLocaleFromLanguage(language) : convertToContentfulLocale(locale),
    [language, locale]
  );
  const [isDocusignModalOpen, setIsDocusignModalOpen] = useState(false);
  const [isSendingDocusignModalOpen, setIsSendingDocusignModalOpen] = useState(false);
  const [isDocusignSending, setIsDocusignSending] = useState(false);
  const [isDocusignSent, setIsDocusignSent] = useState(false);
  const [docusignStatus, setDocusignStatus] = useState<string | undefined>(initialDocusignStatus);
  const [docusignSentDate, setDocusignSentDate] = useState<string | undefined>(
    checkoutData?.sharedDate
  );
  const [quoteStatus, setQuoteStatus] = useState<string | undefined>(currentStatus);

  // Sync docusignStatus, quoteStatus, and docusignSentDate when props change (e.g., user selects different quote from list)
  useEffect(() => {
    setDocusignStatus(initialDocusignStatus);
    setQuoteStatus(currentStatus);
    setDocusignSentDate(checkoutData?.sharedDate);
  }, [currentStatus, initialDocusignStatus, checkoutData?.sharedDate]);

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [successModalMessage, setSuccessModalMessage] = useState<string>('');
  const [createdQuoteIdForRedirect, setCreatedQuoteIdForRedirect] = useState<string | null>(null);
  const [lastUploadedFileId, setLastUploadedFileId] = useState<string>();
  const [hasCompanySelected, setHasCompanySelected] = useState(false);
  const [hasFinanceCompanySelected, setHasFinanceCompanySelected] = useState(false);
  // Store full business entity objects for D&B data
  const [selectedCompanyEntity, setSelectedCompanyEntity] = useState<BusinessSearchResult | null>(
    null
  );
  const [selectedFinanceEntity, setSelectedFinanceEntity] = useState<BusinessSearchResult | null>(
    null
  );

  // Initialize with default values from summary or empty
  const [_currentProspectType, setCurrentProspectType] = useState<string>(
    prospectDetails?.quoteId ? '' : 'Business' // Default to Business for new quotes
  );
  const [_currentPaymentMethod, setCurrentPaymentMethod] = useState<string>(
    checkoutData?.summary?.preferredPaymentMethod || ''
  );

  const { mutate: updateQuote, isPending: isUpdatePending } = useUpdateQuote({
    prospectId: prospectDetails?.prospectId ?? '',
    quoteId: prospectDetails?.quoteId ?? '',
  });

  const { mutate: createQuote, isPending: isCreatePending } = useCreateQuote({
    prospectId: prospectDetails?.prospectId ?? '',
  });

  const { refetch: downloadQuote, isFetching: isDownloadPending } = useDownloadQuote({
    prospectId: prospectDetails?.prospectId ?? '',
    quoteId: savedQuoteId ?? '',
    language: locale?.split('-')[0],
    enabled: false,
  });

  const { mutate: sendDocusign, isPending: isSendDocusignPending } = useSendDocusign({
    prospectId: prospectDetails?.prospectId ?? '',
    quoteId: savedQuoteId ?? '',
  });

  // File upload hook for bank quotation uploads
  const { getUploadUrl, handleFileRemove } = useFileUpload({
    customerId: prospectDetails?.prospectId,
    category: DOCUMENT_CATEGORY.BANK_QUOTATION,
    entityType: FILEHUB_MODULE_NAME.QUOTE_MANAGEMENT,
    entityId: savedQuoteId || prospectDetails?.prospectId || 'unknown',
    onUploadUrlSuccess: (response, file) => {
      setLastUploadedFileId(response.fileId);

      // Persist file metadata to backend after successful S3 upload
      const metadata: UploadQuoteDocumentRequest = {
        fileId: response.fileId,
        tenantId: response.tenantId,
        customerId: response.customerId,
        moduleName: 'Quote Management',
        moduleReferenceIdentifier: 'quoteId',
        category: DOCUMENT_CATEGORY.BANK_QUOTATION,
        fileNameOriginal: response.fileNameOriginal,
        fileNameSanitized: response.fileNameSanitized,
        mimeType: response.mimeType || file.type || 'application/octet-stream',
        documentCategory: DOCUMENT_CATEGORY.BANK_QUOTATION,
        createdAt: response.createdAt || new Date().toISOString(),
      };

      setDocumentMetadata(metadata);
    },
  });

  /**
   * Handles document click for downloading uploaded files.
   * Fetches presigned download URL from FileHub and triggers download.
   */
  const handleDocumentClick = useCallback(async (fileId: string, fileName: string) => {
    try {
      const response = await FileHubApi.instance.get<string>(
        `sales/X/v1/files/${fileId}/download-url`,
        { params: { module_name: FILEHUB_MODULE_NAME.QUOTE_MANAGEMENT } }
      );
      const downloadUrl = response.data;

      if (downloadUrl) {
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          link.remove();
        }, 100);
      }
    } catch (error) {
      console.error('Failed to download file:', error);
    }
  }, []);

  /**
   * Handles upload completion - marks form as dirty to enable save button.
   */
  const handleUploadComplete = useCallback((fileUrl: string) => {
    formRef.current?.setValue('uploadBankQuotation', fileUrl, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, []);

  // Vehicle availability check for existing quotes
  const { mutateAsync: checkVehicleByShortcode } = useGetCheckoutByShortcode();

  useEffect(() => {
    const shortcode = checkoutData?.shortcode;
    if (!prospectDetails?.quoteId || !shortcode) return;

    checkVehicleByShortcode({ shortCode: shortcode }).catch((error: unknown) => {
      if (
        error instanceof Error &&
        'errorCode' in error &&
        (error as Error & { errorCode: string }).errorCode ===
          CHECKOUT_ERROR_CODES.VEHICLE_IS_NOT_AVAILABLE
      ) {
        setIsVehicleUnavailable(true);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutData?.shortcode, prospectDetails?.quoteId]);

  const { summary } = checkoutData || {};
  const effectivePricingSubtotal = useMemo(
    () =>
      Math.round(
        ((checkoutData?.pricing?.configurationSubtotal ?? checkoutData?.pricing?.subtotal ?? 0) *
          100) /
          100
      ),
    [checkoutData?.pricing?.configurationSubtotal, checkoutData?.pricing?.subtotal]
  );

  // Compute effective payment method from form state, or fallback to summary data
  const effectivePaymentMethod = useMemo(() => {
    // Prioritize user selection from form (tracked via useSubscribe)
    if (_currentPaymentMethod) {
      return _currentPaymentMethod;
    }
    // Fallback to API data from summary
    return summary?.preferredPaymentMethod || '';
  }, [_currentPaymentMethod, summary?.preferredPaymentMethod]);

  const {
    data: incentiveLimits,
    isFetching: isIncentiveFetching,
    isError: isIncentiveError,
  } = useGetIncentiveLimits({
    paymentType: effectivePaymentMethod,
    model: (checkoutData?.model || vehicle?.model || '').replace(/^Lucid\s+/i, ''),
    trim: checkoutData?.trim || vehicle?.trim || '',
    modelYear: Number(checkoutData?.modelYear || vehicle?.year),
  });
  const incentiveUnavailable = isIncentiveFetching || isIncentiveError || !incentiveLimits;
  const incentiveStatusMessage = isIncentiveFetching
    ? (labels?.['checkout.incentives.loading'] ?? 'Loading discount limits. Please wait.')
    : (labels?.['checkout.incentives.unavailable'] ??
      'Discount limits are unavailable for this payment method and vehicle. Please retry or contact Lucid Order Ops.');

  // Lenders are configured per financing product, e.g. "LFS Lease" resolves to "Lease".
  const { data: lenders = [] } = useGetLenders({
    financeType: effectivePaymentMethod.startsWith('LFS ') ? effectivePaymentMethod.slice(4) : '',
    customerType: _currentProspectType || summary?.prospectType,
  });

  const financeProviderOptions = useMemo(() => {
    const options = lenders.map((lender) => ({
      label: lender.lenderName,
      value: lender.lenderName,
    }));
    const savedProvider = summary?.financeProvider;
    // Keep the saved provider selectable when FinOps doesn't return it.
    return savedProvider && !options.some((option) => option.value === savedProvider)
      ? [...options, { label: savedProvider, value: savedProvider }]
      : options;
  }, [lenders, summary?.financeProvider]);

  useEffect(() => {
    setHasCompanySelected(Boolean(summary?.companyName));
    setHasFinanceCompanySelected(Boolean(summary?.financeCompanyName));

    // Populate selectedCompanyEntity from DUNS data when editing a quote
    if (summary?.duns?.dunsNumber) {
      const companyEntity: BusinessSearchResult = {
        duns: summary.duns.dunsNumber || '',
        name: summary.duns.companyName || summary.companyName || '',
        primaryName: summary.duns.primaryName || summary.companyName || '',
        isOutOfBusiness: summary.duns.isOutOfBusiness ?? false,
        address: summary.invoiceAddressLine1 || '',
        city: summary.invoiceCity || '',
        postalCode: summary.invoicePostalCode || '',
        country: summary.invoiceCountry || '',
        countryCode: summary.invoiceCountry || '',
        region: '',
        displayText: summary.duns.companyName || summary.companyName || '',
      };
      setSelectedCompanyEntity(companyEntity);
    } else {
      setSelectedCompanyEntity(null);
    }

    // Populate selectedFinanceEntity from DUNS data when editing a quote
    if (summary?.financeDuns?.dunsNumber) {
      const financeEntity: BusinessSearchResult = {
        duns: summary.financeDuns.dunsNumber || '',
        name: summary.financeDuns.companyName || summary.financeCompanyName || '',
        primaryName: summary.financeDuns.primaryName || summary.financeCompanyName || '',
        isOutOfBusiness: summary.financeDuns.isOutOfBusiness ?? false,
        address: summary.financeCompanyAddress || '',
        city: summary.financeCity || '',
        postalCode: summary.financePostalCode || '',
        country: summary.financeCountry || '',
        countryCode: summary.financeCountry || '',
        region: '',
        displayText: summary.financeDuns.companyName || summary.financeCompanyName || '',
      };
      setSelectedFinanceEntity(financeEntity);
    } else {
      setSelectedFinanceEntity(null);
    }
  }, [
    summary?.companyName,
    summary?.financeCompanyName,
    summary?.duns,
    summary?.financeDuns,
    summary?.invoiceAddressLine1,
    summary?.invoiceCity,
    summary?.invoiceCountry,
    summary?.invoicePostalCode,
    summary?.financeCompanyAddress,
    summary?.financeCity,
    summary?.financeCountry,
    summary?.financePostalCode,
  ]);

  const setFormValue = useCallback((name: string, value: unknown) => {
    formRef.current?.setValue(name, value, { shouldValidate: true, shouldDirty: true });
  }, []);

  const handleCompanyChange = useCallback(
    (business: BusinessSearchResult | null) => {
      applyBusinessSelection(business, setFormValue, {
        name: 'companyName',
        duns: 'companyDuns',
        address: 'invoiceAddress',
        postalCode: 'businessPostalCode',
        city: 'businessCity',
        country: 'invoiceCountry',
      });
      setHasCompanySelected(Boolean(business));
      setSelectedCompanyEntity(business);
    },
    [setFormValue]
  );

  const handleFinanceCompanyChange = useCallback(
    (business: BusinessSearchResult | null) => {
      applyBusinessSelection(business, setFormValue, {
        name: 'financeCompanyName',
        duns: 'financeDuns',
        address: 'financeCompanyAddress',
        postalCode: 'financePostalCode',
        city: 'financeCity',
        country: 'financeCountry',
      });
      setHasFinanceCompanySelected(Boolean(business));
      setSelectedFinanceEntity(business);
    },
    [setFormValue]
  );

  // Calculate discount/subvention exceed info in a single memoization
  const { discountExceedInfo, agentSubventionExceedInfo } = useMemo(() => {
    const subtotal = effectivePricingSubtotal;
    const maximumPercentage = incentiveLimits?.totalPossibleDiscount ?? 0;
    const maxThreshold = Math.round((maximumPercentage / 100) * subtotal * 100) / 100;

    // Cash/Percentage discount calculations
    const cashDiscountValue = parseLocalePrice(discountValues.cashDiscount);
    const percentageDiscountValue = parseLocalePrice(discountValues.percentageDiscount);
    const isCashDiscountUsed = cashDiscountValue > 0;
    const isDiscountExceeded =
      cashDiscountValue > maxThreshold ||
      percentageDiscountValue > maximumPercentage ||
      (!!incentiveLimits &&
        exceedsDiscountBreakdownLimit(
          subtotal,
          {
            agentDiscountAmount: discountValues.agentDiscountAmount,
            agentDiscountPercentage: discountValues.agentDiscountPercentage,
            lucidDiscountAmount: discountValues.lucidDiscountAmount,
            lucidDiscountPercentage: discountValues.lucidDiscountPercentage,
          },
          incentiveLimits
        ));

    // Agent subvention calculations
    const agentSubventionValue = parseLocalePrice(discountValues.agentSubventionAmount);
    const isSubventionExceeded = agentSubventionValue > maxThreshold;

    return {
      discountExceedInfo: {
        isExceeded: isDiscountExceeded,
        maxDiscountValue: maxThreshold,
        isCashDiscountUsed,
      },
      agentSubventionExceedInfo: {
        isExceeded: isSubventionExceeded,
        maxAgentSubventionValue: maxThreshold,
      },
    };
  }, [
    discountValues.cashDiscount,
    discountValues.percentageDiscount,
    discountValues.agentSubventionAmount,
    discountValues.agentDiscountAmount,
    discountValues.agentDiscountPercentage,
    discountValues.lucidDiscountAmount,
    discountValues.lucidDiscountPercentage,
    effectivePricingSubtotal,
    incentiveLimits,
  ]);

  const formFields = useMemo(
    () =>
      createQuoteFormFields(summary, {
        currentStatus: quoteStatus,
        docusignStatus,
        isPercentageDiscountExceed: discountExceedInfo.isExceeded,
        maxDiscountValue: discountExceedInfo.maxDiscountValue,
        isCashDiscountUsed: discountExceedInfo.isCashDiscountUsed,
        isAgentSubventionExceed: agentSubventionExceedInfo.isExceeded,
        maxAgentSubventionValue: agentSubventionExceedInfo.maxAgentSubventionValue,
        currencySymbol: getCurrencySymbol(getCurrencyCodeForLocale(locale)),
        locale,
        hasCompanySelected,
        hasFinanceCompanySelected,
        onCompanyChange: handleCompanyChange,
        onFinanceCompanyChange: handleFinanceCompanyChange,
        labels,
        quoteFilesMetadata: checkoutData?.quoteFilesMetadata || [],
        financeProviderOptions,
        theme,
        configurationSubtotal: effectivePricingSubtotal,
        modelYear: checkoutData?.modelYear || vehicle?.year,
        getUploadUrl,
        onFileRemove: handleFileRemove,
        onDocumentClick: handleDocumentClick,
        onUploadComplete: handleUploadComplete,
        onDiscountGuideClick: handleDiscountGuideClick,
        isDiscountGuideLoading,
        preferredPaymentMethod: effectivePaymentMethod,
        incentiveLimits,
        incentiveUnavailable,
      }),
    [
      summary,
      effectivePaymentMethod,
      incentiveLimits,
      incentiveUnavailable,
      quoteStatus,
      docusignStatus,
      discountExceedInfo,
      agentSubventionExceedInfo,
      locale,
      hasCompanySelected,
      hasFinanceCompanySelected,
      handleCompanyChange,
      handleFinanceCompanyChange,
      labels,
      checkoutData?.quoteFilesMetadata,
      financeProviderOptions,
      checkoutData?.modelYear,
      vehicle?.year,
      effectivePricingSubtotal,
      theme,
      getUploadUrl,
      handleFileRemove,
      handleDocumentClick,
      handleUploadComplete,
      handleDiscountGuideClick,
      isDiscountGuideLoading,
    ]
  );

  /**
   * Handles successful form submission
   * @param {unknown} data - Form data from the submission
   */
  const handleSuccess = useCallback(
    (data: unknown) => {
      setErrorMessage(null);
      const formData = data as QuoteFormData;
      if (
        incentiveUnavailable ||
        !incentiveLimits ||
        formData.preferredPaymentMethod !== effectivePaymentMethod
      ) {
        setErrorMessage(incentiveStatusMessage);
        return;
      }

      // Calculate agency discount cap values
      const discountTotals = resolveDiscountTotals(formData);
      const cashDiscountValue = parseLocalePrice(discountTotals.cashDiscount);
      const percentageDiscountValue = parseLocalePrice(discountTotals.percentageDiscount);
      const agentSubventionValue = parseLocalePrice(formData.agentSubventionAmount);
      const maximumAgentPercentage = incentiveLimits.maximumAgentDiscountPercentage;
      const maximumLucidPercentage = incentiveLimits.maximumLucidDiscountPercentage;
      const maximumTotalPercentage = incentiveLimits.totalPossibleDiscount;

      // Calculate price adjustment (same logic as VehicleCard)
      const configSubtotal = effectivePricingSubtotal;

      if (
        exceedsDiscountBreakdownLimit(configSubtotal, formData, incentiveLimits) ||
        exceedsPartnerCompensationLimit(
          configSubtotal,
          cashDiscountValue,
          percentageDiscountValue,
          agentSubventionValue,
          incentiveLimits.totalPossibleDiscount
        )
      ) {
        setErrorMessage(
          (
            labels?.['checkout.discountNote.percentage'] ??
            `Discount limits exceeded: Agent ${maximumAgentPercentage}%, Lucid ${maximumLucidPercentage}%, Total ${maximumTotalPercentage}%. Please contact Lucid Order Ops if a higher discount is required: PartnerSupport-EU@lucidmotors.com.`
          )
            .replace(/{{\s*maximumAgentPercentage\s*}}/g, String(maximumAgentPercentage))
            .replace(/{{\s*maximumLucidPercentage\s*}}/g, String(maximumLucidPercentage))
            .replace(/{{\s*maximumTotalPercentage\s*}}/g, String(maximumTotalPercentage))
        );
        return;
      }

      const priceAdjustmentValue = resolvePriceAdjustmentValue(
        formData.preferredPaymentMethod,
        configSubtotal,
        cashDiscountValue,
        percentageDiscountValue,
        agentSubventionValue
      );

      // Calculate VAT and grand total (same logic as VehicleCard)
      const vatCalculation = calculatePricing(
        buildGrandTotalCalculationParams(checkoutData?.pricing, {
          priceAdjustmentValue,
          preferredPaymentMethod: formData.preferredPaymentMethod,
          requestedIncentives: formData.requestedIncentives,
        })
      );
      const agencyDiscountPercentageCap = incentiveLimits.totalPossibleDiscount;
      const agencyCashDiscountCap =
        Math.round((agencyDiscountPercentageCap / 100) * configSubtotal * 100) / 100;

      // Calculate extra amount beyond cap based on payment method
      const isLoanMethod = LOAN_PAYMENT_METHODS.includes(formData.preferredPaymentMethod);

      // For Loan/Leasing: use agentSubventionAmount
      // For Cash/3rd Party Financing: use cashDiscount or percentageDiscount (converted to amount)
      const percentageDiscountAsAmount =
        Math.round((percentageDiscountValue / 100) * configSubtotal * 100) / 100;
      const agencyExtraCashDiscount = isLoanMethod
        ? Math.round(Math.max(0, agentSubventionValue - agencyCashDiscountCap) * 100) / 100
        : Math.round(
            Math.max(0, cashDiscountValue + percentageDiscountAsAmount - agencyCashDiscountCap) *
              100
          ) / 100;

      const vin = vehicle?.vin || checkoutData?.vin;
      const vinBatchNumber = vehicle?.batchNumber || checkoutData?.vinBatchNumber;
      const expirationDate = checkoutData?.expirationDate;
      const { subventionMethod, dunsNumber } = resolveLenderFields(
        formData.financeProvider,
        lenders,
        summary
      );

      // Build payload based on form data
      const payload = {
        ...(vin ? { vin } : {}),
        ...(vinBatchNumber ? { vinBatchNumber } : {}),
        ...(expirationDate ? { expirationDate } : {}),
        prospectType: formData.prospectType,
        preferredPaymentMethod: formData.preferredPaymentMethod,
        deliveryTimeline: formatToUTCDate(formData.deliveryTimeline),
        ...buildBusinessInfo(formData, selectedCompanyEntity),
        ...buildFinancingDetails(formData, selectedFinanceEntity),
        ...(LOAN_PAYMENT_METHODS.includes(formData.preferredPaymentMethod) && {
          quotationDetails: {
            uploadBankQuotation: lastUploadedFileId,
            quoteId: formData.quoteId,
            financeProvider: formData.financeProvider,
            subventionMethod,
            locale: getCountryCodeFromLocale(locale),
            dunsNumber,
            agentSubventionAmount: formData.agentSubventionAmount,
            monthlyPaymentInclVat: formData.monthlyPaymentInclVat,
            downpayment: formData.downpayment,
            agencyDiscountPercentageCap: agencyDiscountPercentageCap.toString(),
            agencyCashDiscountCap: agencyCashDiscountCap.toString(),
            agencyExtraCashDiscount: agencyExtraCashDiscount.toString(),
          },
        }),
        discountsAndIncentivesDetails: {
          cashDiscount: discountTotals.cashDiscount,
          agentDiscountAmount: formData.agentDiscountAmount || null,
          agentDiscountPercentage: formData.agentDiscountPercentage || null,
          lucidDiscountAmount: formData.lucidDiscountAmount || null,
          lucidDiscountPercentage: formData.lucidDiscountPercentage || null,
          requestedIncentives: {
            charging_voucher: hasChargingCreditOfferSelected(formData.requestedIncentives),
            trade_in_support: hasTradeInSupportSelected({
              preferredPaymentMethod: formData.preferredPaymentMethod,
              requestedIncentives: formData.requestedIncentives,
            }),
          },
          percentageDiscount: discountTotals.percentageDiscount,
          agencyDiscountPercentageCap: agencyDiscountPercentageCap.toString(),
          agencyCashDiscountCap: agencyCashDiscountCap.toString(),
          agencyExtraCashDiscount: agencyExtraCashDiscount.toString(),
        },
        ...(documentMetadata ? { documentMetadata } : {}),
      };

      // Merge payload with checkoutData, updating pricing.total with discounted value
      const mergedPayload = {
        ...payload,
        vehicleDetails: {
          ...checkoutData,
          pricing: {
            ...checkoutData?.pricing,
            configurationSubtotal:
              Math.round((checkoutData?.pricing?.configurationSubtotal ?? 0) * 100) / 100,
            ...(checkoutData?.pricing?.taxAmount !== undefined && {
              taxAmount: vatCalculation.vatAmount,
            }),
            total: vatCalculation.grandTotal,
          },
        },
      };
      if (prospectDetails?.prospectId && prospectDetails?.quoteId) {
        updateQuote(mergedPayload, {
          onSuccess: (response) => {
            setSuccessModalMessage(labels?.['checkout.success.update'] ?? SUCCESS_UPDATE_MESSAGE);
            setIsSuccessModalOpen(true);
            setIsQuoteSaved(true);
            if (response?.quoteIdentifier) {
              setSavedQuoteId(response.quoteIdentifier);
            }
            // Clear document metadata after successful save
            setDocumentMetadata(undefined);
          },
          onError: () => {
            setErrorMessage(
              labels?.['checkout.error.update'] ?? 'Failed to update quote. Please try again.'
            );
          },
        });
      } else {
        createQuote(mergedPayload, {
          onSuccess: (response) => {
            setSuccessModalMessage(labels?.['checkout.success.create'] ?? SUCCESS_MESSAGE);
            setIsSuccessModalOpen(true);
            setIsQuoteSaved(true);
            if (response?.quoteIdentifier) {
              setSavedQuoteId(response.quoteIdentifier);
              // Store quote ID for redirect after modal closes
              setCreatedQuoteIdForRedirect(response.quoteIdentifier);
            }
            // Clear document metadata after successful save
            setDocumentMetadata(undefined);
          },
          onError: () => {
            setErrorMessage(
              labels?.['checkout.error.create'] ?? 'Failed to create quote. Please try again.'
            );
          },
        });
      }
    },
    [
      prospectDetails,
      checkoutData,
      updateQuote,
      createQuote,
      lastUploadedFileId,
      labels,
      vehicle,
      documentMetadata,
      selectedCompanyEntity,
      selectedFinanceEntity,
      effectivePricingSubtotal,
      lenders,
      summary,
      locale,
      incentiveLimits,
      incentiveUnavailable,
      incentiveStatusMessage,
      effectivePaymentMethod,
    ]
  );

  /**
   * Handles form validation errors
   * @param {unknown} error - The validation error
   */
  const handleError = useCallback(
    (_error: unknown) => {
      setErrorMessage(labels?.['checkout.validation.error'] ?? VALIDATION_ERROR);
    },
    [labels]
  );

  /**
   * Handles form dirty state changes
   * This is only called once when the form first becomes dirty
   */
  const handleFormDirtyChange = useCallback(() => {
    // The onDirtyChange callback only fires once when form becomes dirty
    // Discount values are tracked via the useSubscribe hook below
  }, []);

  // Track discount input changes in real-time via form subscription
  useSubscribe(
    formRef,
    (values, { name }) => {
      const discountUpdates = syncDiscountFields(name, values);
      if (discountUpdates) {
        Object.entries(discountUpdates).forEach(([field, value]) => {
          formRef.current?.setValue(field, value, { shouldValidate: true, shouldDirty: true });
        });
        const merged = { ...values, ...discountUpdates };
        setDiscountValues((prev) => ({
          ...prev,
          cashDiscount: merged[QUOTE_FORM_FIELDS.CASH_DISCOUNT] || undefined,
          percentageDiscount: merged[QUOTE_FORM_FIELDS.PERCENTAGE_DISCOUNT] || undefined,
          agentDiscountAmount: merged[QUOTE_FORM_FIELDS.AGENT_DISCOUNT_AMOUNT] || undefined,
          agentDiscountPercentage: merged[QUOTE_FORM_FIELDS.AGENT_DISCOUNT_PERCENTAGE] || undefined,
          lucidDiscountAmount: merged[QUOTE_FORM_FIELDS.LUCID_DISCOUNT_AMOUNT] || undefined,
          lucidDiscountPercentage: merged[QUOTE_FORM_FIELDS.LUCID_DISCOUNT_PERCENTAGE] || undefined,
        }));
      } else if (name === QUOTE_FORM_FIELDS.CASH_DISCOUNT) {
        setDiscountValues((prev) => ({
          ...prev,
          cashDiscount: values[QUOTE_FORM_FIELDS.CASH_DISCOUNT] || undefined,
        }));
      } else if (name === QUOTE_FORM_FIELDS.PERCENTAGE_DISCOUNT) {
        setDiscountValues((prev) => ({
          ...prev,
          percentageDiscount: values[QUOTE_FORM_FIELDS.PERCENTAGE_DISCOUNT] || undefined,
        }));
      } else if (name === QUOTE_FORM_FIELDS.AGENT_SUBVENTION_AMOUNT) {
        setDiscountValues((prev) => ({
          ...prev,
          agentSubventionAmount: values[QUOTE_FORM_FIELDS.AGENT_SUBVENTION_AMOUNT] || undefined,
        }));
      } else if (name === QUOTE_FORM_FIELDS.REQUESTED_INCENTIVES) {
        const selected = values[QUOTE_FORM_FIELDS.REQUESTED_INCENTIVES];
        setRequestedIncentives(Array.isArray(selected) ? selected : []);
      } else if (name === QUOTE_FORM_FIELDS.PREFERRED_PAYMENT_METHOD) {
        const selectedValue = values[QUOTE_FORM_FIELDS.PREFERRED_PAYMENT_METHOD];
        setCurrentPaymentMethod(selectedValue || '');

        if (!selectedValue || !PAYMENT_METHOD_VALUES.includes(selectedValue)) return;

        if (DISCOUNT_ENABLED_PAYMENT_METHODS.includes(selectedValue)) {
          // Switching to Cash/3rd Party Financing - restore API discount values, clear agent subvention
          setDiscountValues((prev) => ({
            ...prev,
            cashDiscount: apiDiscountValuesRef.current.cashDiscount,
            percentageDiscount: apiDiscountValuesRef.current.percentageDiscount,
            agentDiscountAmount: apiDiscountValuesRef.current.agentDiscountAmount,
            agentDiscountPercentage: apiDiscountValuesRef.current.agentDiscountPercentage,
            lucidDiscountAmount: apiDiscountValuesRef.current.lucidDiscountAmount,
            lucidDiscountPercentage: apiDiscountValuesRef.current.lucidDiscountPercentage,
            agentSubventionAmount: undefined,
          }));
        } else if (selectedValue.toLowerCase() === 'lfs lease') {
          // LFS Lease - clear all discount values (no price adjustment for LFS Lease)
          setDiscountValues((prev) => ({
            ...prev,
            cashDiscount: undefined,
            percentageDiscount: undefined,
            agentDiscountAmount: undefined,
            agentDiscountPercentage: undefined,
            lucidDiscountAmount: undefined,
            lucidDiscountPercentage: undefined,
            agentSubventionAmount: undefined,
          }));
        } else {
          // Switching to LFS Loan etc - preserve user-entered agent subvention from form field,
          // fall back to current state or API value, and clear cash/percentage discounts
          const currentFormSubvention = values[QUOTE_FORM_FIELDS.AGENT_SUBVENTION_AMOUNT];
          setDiscountValues((prev) => ({
            ...prev,
            cashDiscount: undefined,
            percentageDiscount: undefined,
            agentDiscountAmount: undefined,
            agentDiscountPercentage: undefined,
            lucidDiscountAmount: undefined,
            lucidDiscountPercentage: undefined,
            agentSubventionAmount:
              currentFormSubvention ||
              prev.agentSubventionAmount ||
              apiDiscountValuesRef.current.agentSubventionAmount,
          }));
        }
      } else if (name === QUOTE_FORM_FIELDS.PROSPECT_TYPE) {
        setCurrentProspectType(values[QUOTE_FORM_FIELDS.PROSPECT_TYPE] || '');
      }
    },
    []
  );

  // Initialize discount values from API data when checkoutData loads
  useEffect(() => {
    if (summary) {
      const apiValues = {
        cashDiscount: summary.cashDiscount || undefined,
        percentageDiscount: summary.percentageDiscount || undefined,
        agentSubventionAmount: summary.agentSubventionAmount || undefined,
        agentDiscountAmount: summary.agentDiscountAmount || undefined,
        agentDiscountPercentage: summary.agentDiscountPercentage || undefined,
        lucidDiscountAmount: summary.lucidDiscountAmount || undefined,
        lucidDiscountPercentage: summary.lucidDiscountPercentage || undefined,
      };
      // Store original API values for restoration when switching payment methods
      apiDiscountValuesRef.current = apiValues;

      // For LFS Lease, clear discount values (no price adjustment)
      if (summary.preferredPaymentMethod?.toLowerCase() === 'lfs lease') {
        setDiscountValues({
          cashDiscount: undefined,
          percentageDiscount: undefined,
          agentSubventionAmount: undefined,
        });
      } else {
        setDiscountValues(apiValues);
      }

      setRequestedIncentives(mapRequestedIncentivesToArray(summary.requestedIncentives));
    }
  }, [summary]);

  // Initialize prospect type and payment method from summary defaults or form defaults
  useEffect(() => {
    // Set from summary if available (edit mode)
    if (summary?.preferredPaymentMethod) {
      setCurrentPaymentMethod(summary.preferredPaymentMethod);
    }
    if (summary?.prospectType) {
      setCurrentProspectType(summary.prospectType);
    }
  }, [summary?.preferredPaymentMethod, summary?.prospectType]);

  /**
   * Opens the Send Docusign modal
   */
  const handleClickOnSendDocusign = useCallback(() => {
    setIsDocusignModalOpen(true);
  }, []);

  /**
   * Closes the Send Docusign modal
   */
  const handleCloseDocusignModal = useCallback(() => {
    setIsDocusignModalOpen(false);
  }, []);

  /**
   * Closes the Sending Docusign modal
   */
  const handleCloseSendingDocusignModal = useCallback(() => {
    setIsSendingDocusignModalOpen(false);
    setIsDocusignSending(false);
  }, []);

  /**
   * Handles the Send Docusign form submission
   * @param data - Form data from the Send Docusign modal
   */
  const handleSendDocusign = useCallback(
    (data: SendDocusignFormData) => {
      // Close form modal and open sending modal
      setIsDocusignModalOpen(false);
      setIsSendingDocusignModalOpen(true);
      setIsDocusignSending(true);
      setIsDocusignSent(false); // Reset sent state at start of new send

      const isBusinessProspect =
        getFormValues('prospectType') === 'Business' || summary?.prospectType === 'Business';

      sendDocusign(
        {
          ...(isBusinessProspect
            ? { businessName: data.businessName }
            : { firstName: data.firstName, lastName: data.lastName }),
          email: data.email,
        },
        {
          onSuccess: (response) => {
            setIsDocusignSending(false);
            setIsDocusignSent(true);
            const newStatus = response?.status || 'Shared';
            // Use response status or default to 'Shared'
            setDocusignStatus(newStatus);
            // Update the quote status based on the response
            setQuoteStatus(newStatus);
            // Capture the current timestamp when docusign is sent
            setDocusignSentDate(formatToOffsetDateTime(new Date()));
          },
          onError: () => {
            setIsDocusignSending(false);
            setIsSendingDocusignModalOpen(false);
            setErrorMessage('Failed to send Docusign. Please try again.');
          },
        }
      );
    },
    [sendDocusign, getFormValues, summary?.prospectType]
  );

  /**
   * Handles downloading the quotation as a PDF
   */
  const handleDownloadQuotation = useCallback(async () => {
    const { data } = await downloadQuote();
    if (!data) return;

    const { blob, filename } = data;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }, [downloadQuote]);

  // Memoize form loading state to prevent recalculation
  const isFormLoading = useMemo(
    () => isUpdatePending || isCreatePending || isDownloadPending || isSendDocusignPending,
    [isUpdatePending, isCreatePending, isDownloadPending, isSendDocusignPending]
  );

  // Memoize docusign sent status check
  const isDocusignAlreadySent = useMemo(
    () => (quoteStatus ? DOCUSIGN_SENT_STATUSES.includes(quoteStatus) : false),
    [quoteStatus]
  );

  const pageTitle = useMemo(
    () =>
      prospectDetails?.prospectName
        ? (labels?.['checkout.pageTitle.withProspect']?.replace(
            '{{prospectName}}',
            prospectDetails.prospectName
          ) ?? `Quotation for ${prospectDetails.prospectName}`)
        : (labels?.['checkout.pageTitle.default'] ?? 'Vehicle Information'),
    [prospectDetails?.prospectName, labels]
  );

  // Show loader while initial data is loading
  if (isLoading) {
    return (
      <Box data-testid={dataTestId} className={styles.checkoutContainer}>
        <Box className={styles.loaderWrapper}>
          <Loader size={48} theme={theme} />
        </Box>
      </Box>
    );
  }

  // Show error state when API fails or no checkout data
  if (error || !checkoutData) {
    return (
      <Box data-testid={dataTestId} className={styles.checkoutContainer}>
        <Box className={styles.noDataContainer}>
          <Button
            type="tertiary"
            color="neutral"
            theme={theme}
            dataTestId="back-button"
            gaComponent="checkout"
            label={labels?.['checkout.back'] ?? 'Back'}
            iconLeft={{ category: 'arrows', name: 'arrow-left' }}
            handleClick={onBack}
          />
          <Typography variant="body" size="medium" color="error-1" theme={theme}>
            {error ||
              (prospectDetails?.quoteId
                ? (labels?.['checkout.error.noData.quoteId']?.replace(
                    '{{quoteId}}',
                    prospectDetails.quoteId
                  ) ?? `No checkout data available for Quote ID: ${prospectDetails.quoteId}`)
                : vehicle?.vin
                  ? (labels?.['checkout.error.noData.vin']?.replace('{{vin}}', vehicle.vin) ??
                    `No checkout data available for Vehicle VIN: ${vehicle.vin}`)
                  : (labels?.['checkout.error.noData.default'] ??
                    'Failed to load checkout data. Please try again.'))}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Render the quote creation form
  return (
    <Box data-testid={dataTestId} className={styles.pageContainer}>
      <Typography variant="headline" size="small" color="primary-1" theme={theme}>
        {pageTitle}
      </Typography>
      <Box pt={8} mb={8}>
        <Typography variant="body" size="medium" color="gray-5" theme={theme}>
          {labels?.['checkout.savedQuotationsNote'] ??
            'Saved quotations can be reviewed in prospect details.'}
        </Typography>
      </Box>
      <Box className={styles.formContent} mt={32}>
        <Box className={styles.formWrapper}>
          {errorMessage && (
            <Box mb={16} pt={12} pb={12} pl={16} pr={16} className={styles.errorMessageText}>
              <Typography variant="body" size="medium" color="white" theme={theme}>
                {errorMessage}
              </Typography>
            </Box>
          )}
          {isFormLoading && (
            <Box className="fetchingOverlay">
              <Loader size={32} theme={theme} />
            </Box>
          )}

          <Button
            type="tertiary"
            color="neutral"
            theme="light"
            dataTestId="back-button"
            gaComponent="checkout"
            label={labels?.['checkout.back'] ?? 'Back'}
            iconLeft={{ category: 'arrows', name: 'arrow-left' }}
            handleClick={onBack}
          />
          <Row>
            <Col xs={12} md={6}>
              <VehicleCard
                theme={theme}
                checkoutData={checkoutData}
                vehicle={vehicle}
                onSendDocusign={handleClickOnSendDocusign}
                onDownloadQuotation={handleDownloadQuotation}
                quoteId={savedQuoteId}
                docusignStatus={docusignStatus}
                isQuoteSaved={isQuoteSaved}
                currentStatus={quoteStatus}
                isVehicleUnavailable={isVehicleUnavailable}
                cashDiscount={discountValues.cashDiscount}
                percentageDiscount={discountValues.percentageDiscount}
                agentSubventionAmount={discountValues.agentSubventionAmount}
                preferredPaymentMethod={effectivePaymentMethod}
                requestedIncentives={requestedIncentives}
                labels={labels}
              />
            </Col>
            <Col xs={12} md={6}>
              {/* Vehicle Unavailable Banner */}
              {isVehicleUnavailable && (
                <Box
                  className={styles.vehicleUnavailableBanner}
                  data-testid="checkout-vehicle-unavailable-banner"
                >
                  <Box className={styles.vehicleUnavailableContent}>
                    <Iconography category="global" name="information" color="error-1" />
                    <Typography variant="body" size="medium" color="error-1" theme={theme}>
                      {labels?.['checkout.vehicle.unavailable.title'] ?? VEHICLE_UNAVAILABLE_TITLE}
                    </Typography>
                  </Box>
                  <Typography variant="body" size="small" color="black" theme={theme}>
                    {labels?.['checkout.vehicle.unavailable.message'] ??
                      VEHICLE_UNAVAILABLE_MESSAGE}
                  </Typography>
                </Box>
              )}

              {/* Docusign Sent Banner */}
              {isDocusignAlreadySent && (
                <Box
                  className={styles.docusignSentBanner}
                  data-testid="checkout-docusign-sent-banner"
                >
                  <Box className={styles.docusignSentContent}>
                    <Iconography category="global" name="failure" color="success-1" />
                    <Typography variant="body" size="medium" color="success-1" theme={theme}>
                      {labels?.['checkout.docusign.sentTitle'] ?? 'Docusign Sent to Customer'}
                    </Typography>
                  </Box>
                  <Typography variant="body" size="small" color="black" theme={theme}>
                    {labels?.['checkout.docusign.sentMessage'] ??
                      'Docusign has been successfully sent to customer'}
                    {docusignSentDate ? ` (${formatDate(docusignSentDate)})` : ''}.
                  </Typography>
                </Box>
              )}

              <Box mb={24}>
                <Typography variant="subtitle" size="large" theme={theme}>
                  {labels?.['checkout.payment.title'] ?? 'Payment'}
                </Typography>
              </Box>

              <Form
                ref={formRef}
                id="create-quote-form"
                theme={theme === 'dark' ? 'light' : 'dark'}
                formFields={formFields}
                onSuccess={handleSuccess}
                onError={handleError}
                onDirtyChange={handleFormDirtyChange}
                disabled={isVehicleUnavailable}
                disableSubmitUntilDirty={!!prospectDetails?.quoteId}
                isFluid={true}
                locale={languageBasedLocale}
                language={language ?? undefined}
                getUploadUrl={getUploadUrl}
                onFileRemove={handleFileRemove}
                customComponents={{
                  IncentivesHeader,
                  BusinessNameSearch: BusinessNameSearch as React.ComponentType<
                    Record<string, unknown>
                  >,
                  FileUpload: FileUpload as unknown as React.ComponentType<Record<string, unknown>>,
                  DocumentsList: DocumentsList as React.ComponentType<Record<string, unknown>>,
                }}
              />
            </Col>
          </Row>
        </Box>
      </Box>

      <Docusign
        dataTestId="send-docusign-modal"
        isOpen={isDocusignModalOpen}
        onClose={handleCloseDocusignModal}
        onSend={handleSendDocusign}
        theme={theme === 'light' ? 'dark' : 'light'}
        initialValues={{
          prospectType: getFormValues('prospectType') || summary?.prospectType || '',
          companyName: getFormValues('companyName') || summary?.companyName || '',
          firstName: prospectDetails?.firstName ?? '',
          lastName: prospectDetails?.lastName ?? '',
          email: prospectDetails?.email ?? '',
        }}
        labels={labels}
      />

      <CheckoutModal
        dataTestId="sending-docusign-modal"
        isOpen={isSendingDocusignModalOpen}
        onClose={handleCloseSendingDocusignModal}
        theme="light"
        variant={isDocusignSent ? 'sent' : 'sending'}
        showLoader={isDocusignSending}
        labels={labels}
      />

      <CheckoutModal
        dataTestId="save-quote-modal"
        isOpen={isSuccessModalOpen}
        onClose={() => {
          setIsSuccessModalOpen(false);
          // Redirect to checkout URL with quoteId after modal closes (for refresh support)
          if (createdQuoteIdForRedirect) {
            onQuoteCreated?.(createdQuoteIdForRedirect);
            setCreatedQuoteIdForRedirect(null);
          }
        }}
        theme={theme === 'light' ? 'dark' : 'light'}
        variant="success"
        message={successModalMessage}
        labels={labels}
      />
    </Box>
  );
};

Checkout.displayName = 'Checkout';

export default Checkout;
